import React, { useState, useEffect } from 'react';
import { apiRequest } from '@/utils/api-request';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';
import { Smartphone, Loader, CheckCircle, AlertCircle, Plus, X, Edit2 } from 'lucide-react';

interface OnboardedSchool {
  id: number;
  school_name: string;
  client_id: number;
  subscription_tier: 'standard' | 'white_label';
  is_active: boolean;
  created_at: string;
  onboarded_by?: string;
  admin_email?: string;
}

interface School {
  client_id: number;
  slate_name: string;
  cityName: string;
  areaName: string;
  slate_id: number;
}

interface FormData {
  client_id: string;
  school_admin_email: string;
  subscription_tier: 'standard' | 'white_label';
  white_label_app_name?: string;
  white_label_bundle_id?: string;
}

const OnboardParentApp: React.FC = () => {
  const [onboardedSchools, setOnboardedSchools] = useState<OnboardedSchool[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [fetchingOnboarded, setFetchingOnboarded] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [editingSchool, setEditingSchool] = useState<OnboardedSchool | null>(null);

  const [formData, setFormData] = useState<FormData>({
    client_id: '',
    school_admin_email: '',
    subscription_tier: 'standard',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch onboarded schools
  const fetchOnboardedSchools = async () => {
    try {
      setFetchingOnboarded(true);
      const response = await apiRequest('/api/parent-app/admin/schools', { method: 'GET' });
      if (response.success && Array.isArray(response.data)) {
        setOnboardedSchools(response.data);
      }
    } catch (error) {
      console.error('Error fetching onboarded schools:', error);
    } finally {
      setFetchingOnboarded(false);
    }
  };

  // Fetch available schools
  const fetchAvailableSchools = async () => {
    try {
      setFetching(true);
      const response = await apiRequest('/api/reports/admin/schools', { method: 'GET' });
      if (response.success && Array.isArray(response.data)) {
        setSchools(response.data);
      } else {
        showToast('Failed to fetch schools', 'error');
      }
    } catch (error) {
      showToast('Error fetching schools', 'error');
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchOnboardedSchools();
    fetchAvailableSchools();
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.client_id) {
      newErrors.client_id = 'School is required';
    }

    if (!formData.school_admin_email) {
      newErrors.school_admin_email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.school_admin_email)) {
      newErrors.school_admin_email = 'Invalid email format';
    }

    if (formData.subscription_tier === 'white_label') {
      if (!formData.white_label_app_name) {
        newErrors.white_label_app_name = 'App name is required';
      }
      if (!formData.white_label_bundle_id) {
        newErrors.white_label_bundle_id = 'Bundle ID is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const payload: any = {
        client_id: parseInt(formData.client_id),
        school_admin_email: formData.school_admin_email,
        subscription_tier: formData.subscription_tier,
      };

      if (formData.subscription_tier === 'white_label') {
        payload.white_label_app_name = formData.white_label_app_name;
        payload.white_label_bundle_id = formData.white_label_bundle_id;
      }

      const isEditing = !!editingSchool;
      const endpoint = isEditing
        ? `/api/parent-app/admin/schools/${editingSchool.id}`
        : '/api/parent-app/admin/schools/onboard';
      const method = isEditing ? 'PUT' : 'POST';

      const response = await apiRequest(endpoint, { method, body: payload });

      if (response.success) {
        const selectedSchool = schools.find((s) => s.client_id === parseInt(formData.client_id));
        const message = isEditing ? `"${selectedSchool?.slate_name}" updated successfully!` : `"${selectedSchool?.slate_name}" onboarded successfully!`;
        setSuccessMessage(message);
        setShowSuccess(true);
        showToast(message, 'success');

        setTimeout(() => {
          setFormData({
            client_id: '',
            school_admin_email: '',
            subscription_tier: 'standard',
          });
          setEditingSchool(null);
          setShowSuccess(false);
          setShowModal(false);
          fetchOnboardedSchools(); // Refresh list
        }, 2000);
      } else {
        showToast(response.message || 'Failed to save school', 'error');
      }
    } catch (error: any) {
      showToast(error.message || 'Error submitting form', 'error');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    if (!loading) {
      setShowModal(false);
      setFormData({
        client_id: '',
        school_admin_email: '',
        subscription_tier: 'standard',
      });
      setEditingSchool(null);
      setErrors({});
    }
  };

  const openEditModal = (school: OnboardedSchool) => {
    setEditingSchool(school);
    setFormData({
      client_id: school.client_id.toString(),
      school_admin_email: school.admin_email || '',
      subscription_tier: school.subscription_tier,
      white_label_app_name: '',
      white_label_bundle_id: '',
    });
    setShowModal(true);
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brandColor rounded-lg text-white">
              <Smartphone className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-semibold text-gray-900">Parent App Schools</h1>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brandColor text-white">
                  {onboardedSchools.length}
                </span>
              </div>
              <p className="text-xs text-gray-500">Manage onboarded schools</p>
            </div>
          </div>
          <Button
            onClick={() => setShowModal(true)}
          >
            <Plus className="h-4 w-4" />
            Add School
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-4 py-4">
        {fetchingOnboarded ? (
          <div className="flex flex-col items-center justify-center h-full">
            <Loader className="h-8 w-8 animate-spin text-brandColor mb-3" />
            <p className="text-gray-600 text-sm">Loading schools...</p>
          </div>
        ) : onboardedSchools.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="p-3 bg-gray-100 rounded-lg mb-3">
              <Smartphone className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-gray-900 font-medium">No schools onboarded yet</h3>
            <p className="text-gray-500 text-sm mt-1">Get started by adding your first school</p>
            <Button
              onClick={() => setShowModal(true)}
              className="mt-4"
            >
              <Plus className="h-4 w-4" />
              Onboard First School
            </Button>
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <table className="min-w-full">
              <thead className="bg-brandColor text-white sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">School Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Onboarded</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {onboardedSchools.map((school, idx) => (
                  <tr key={school.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-gray-100'}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{school.school_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                        {school.subscription_tier === 'white_label' ? 'White Label' : 'Standard'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        school.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {school.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(school.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => openEditModal(school)}
                        className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors text-gray-600 hover:text-gray-900"
                        title="Edit school"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingSchool ? 'Edit School' : 'Onboard New School'}
              </h2>
              <button
                onClick={closeModal}
                disabled={loading}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {showSuccess ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Success!</h3>
                  <p className="text-gray-600 text-sm">{successMessage}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* School Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      School <span className="text-red-500">*</span>
                    </label>
                    {fetching ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader className="h-5 w-5 animate-spin text-brandColor" />
                      </div>
                    ) : (
                      <>
                        <select
                          name="client_id"
                          value={formData.client_id}
                          onChange={handleInputChange}
                          disabled={!!editingSchool}
                          className={`
                            w-full px-4 py-2 border rounded-lg bg-white text-gray-900
                            ${errors.client_id
                              ? 'border-red-500'
                              : 'border-gray-300'
                            }
                            ${editingSchool ? 'bg-gray-100 cursor-not-allowed' : ''}
                            focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent
                          `}
                        >
                          <option value="">-- Select a school --</option>
                          {schools.map((school) => (
                            <option key={school.client_id} value={school.client_id}>
                              {school.slate_name} • {school.cityName}, {school.areaName}
                            </option>
                          ))}
                        </select>
                        {errors.client_id && (
                          <p className="text-red-500 text-sm mt-1">{errors.client_id}</p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      School Admin Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="school_admin_email"
                      value={formData.school_admin_email}
                      onChange={handleInputChange}
                      placeholder="admin@school.com"
                      disabled={!!editingSchool}
                      className={`
                        w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-500
                        ${errors.school_admin_email
                          ? 'border-red-500'
                          : 'border-gray-300'
                        }
                        ${editingSchool ? 'bg-gray-100 cursor-not-allowed' : ''}
                        focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent
                      `}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {editingSchool ? 'Email cannot be changed' : 'Login credentials will be sent to this email'}
                    </p>
                    {errors.school_admin_email && (
                      <p className="text-red-500 text-sm mt-1">{errors.school_admin_email}</p>
                    )}
                  </div>

                  {/* Subscription Tier */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-3">
                      Subscription Tier <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center p-3 border rounded-lg cursor-pointer transition-all"
                        style={{
                          borderColor: formData.subscription_tier === 'standard' ? 'var(--brand-color, #007bff)' : '#d1d5db',
                          backgroundColor: formData.subscription_tier === 'standard' ? 'rgba(0, 123, 255, 0.03)' : 'transparent'
                        }}
                      >
                        <input
                          type="radio"
                          name="subscription_tier"
                          value="standard"
                          checked={formData.subscription_tier === 'standard'}
                          onChange={handleInputChange}
                          className="h-4 w-4"
                        />
                        <div className="ml-3">
                          <p className="font-medium text-gray-900">Standard</p>
                          <p className="text-xs text-gray-500">Standard white-label version</p>
                        </div>
                      </label>

                      <label className="flex items-center p-3 border rounded-lg cursor-pointer transition-all"
                        style={{
                          borderColor: formData.subscription_tier === 'white_label' ? 'var(--brand-color, #007bff)' : '#d1d5db',
                          backgroundColor: formData.subscription_tier === 'white_label' ? 'rgba(0, 123, 255, 0.03)' : 'transparent'
                        }}
                      >
                        <input
                          type="radio"
                          name="subscription_tier"
                          value="white_label"
                          checked={formData.subscription_tier === 'white_label'}
                          onChange={handleInputChange}
                          className="h-4 w-4"
                        />
                        <div className="ml-3">
                          <p className="font-medium text-gray-900">White Label</p>
                          <p className="text-xs text-gray-500">Custom branded app</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* White Label Fields */}
                  {formData.subscription_tier === 'white_label' && (
                    <div className="border-t pt-5 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          App Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="white_label_app_name"
                          value={formData.white_label_app_name || ''}
                          onChange={handleInputChange}
                          placeholder="e.g., Sunshine Academy Parent App"
                          className={`
                            w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-500
                            ${errors.white_label_app_name
                              ? 'border-red-500'
                              : 'border-gray-300'
                            }
                            focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent
                          `}
                        />
                        {errors.white_label_app_name && (
                          <p className="text-red-500 text-sm mt-1">{errors.white_label_app_name}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Bundle ID <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="white_label_bundle_id"
                          value={formData.white_label_bundle_id || ''}
                          onChange={handleInputChange}
                          placeholder="e.g., com.sunshineacademy.parent"
                          className={`
                            w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-500
                            ${errors.white_label_bundle_id
                              ? 'border-red-500'
                              : 'border-gray-300'
                            }
                            focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent
                          `}
                        />
                        {errors.white_label_bundle_id && (
                          <p className="text-red-500 text-sm mt-1">{errors.white_label_bundle_id}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Info Box */}
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex gap-3">
                    <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <p className="text-xs text-blue-800">
                      A welcome email will be sent to the school admin with login credentials and setup instructions.
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={loading}
                      onClick={closeModal}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader className="h-4 w-4 animate-spin" />
                          {editingSchool ? 'Updating...' : 'Onboarding...'}
                        </>
                      ) : (
                        <>
                          {editingSchool ? (
                            <>
                              <Edit2 className="h-4 w-4" />
                              Update School
                            </>
                          ) : (
                            <>
                              <Plus className="h-4 w-4" />
                              Onboard School
                            </>
                          )}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardParentApp;
